import { useEffect, useRef, useState } from 'react'
import { z } from 'zod'
import { api } from '@api'

// The origin's short locality label (e.g. "Bursledon"), shown in the weather
// row's subtitle. Reverse-geocoded once and cached hard: a rounded
// coordinate's town doesn't change, so this fires at most once per location —
// never on the hourly weather refresh, never per render.
const CACHE_KEY = 'hewey.activities.place'
const THIRTY_DAYS_MS = 30 * 24 * 3_600_000
// Folded into the cached key so a change to how the locality is picked misses
// the existing 30-day entry and refetches, rather than needing a second
// localStorage key. Bump it whenever `localityName` changes.
const CACHE_VERSION = 'v2'

const CacheSchema = z.object({
	key: z.string(),
	ts: z.number(),
	name: z.string().nullable(),
})

const readCache = (key: string): { name: string | null } | null => {
	try {
		const raw = localStorage.getItem(CACHE_KEY)
		if (!raw) return null
		const parsed = CacheSchema.safeParse(JSON.parse(raw))
		if (!parsed.success) return null
		if (parsed.data.key !== key) return null
		if (Date.now() - parsed.data.ts > THIRTY_DAYS_MS) return null
		return { name: parsed.data.name }
	} catch {
		return null
	}
}

const writeCache = (key: string, name: string | null): void => {
	try {
		localStorage.setItem(
			CACHE_KEY,
			JSON.stringify({ key, ts: Date.now(), name }),
		)
	} catch {}
}

// Best-effort: any failure (offline, geocoder down) leaves it null and the row
// simply omits the location. A resolved-but-empty result (no locality) is
// cached as null too, so we don't keep re-asking for a coordinate that has no
// town name.
export const useShortPlaceName = (
	origin: { lat: number; lng: number } | null,
): string | null => {
	const [name, setName] = useState<string | null>(null)
	const handledKey = useRef<string | null>(null)

	useEffect(() => {
		if (!origin) return
		// 2dp ≈ 1.1km — coarse enough to name a town, to reuse the cache across
		// small GPS jitter, and to avoid sending a precise fix upstream.
		const lat = Number(origin.lat.toFixed(2))
		const lng = Number(origin.lng.toFixed(2))
		const key = `${CACHE_VERSION}:${lat},${lng}`
		if (handledKey.current === key) return
		handledKey.current = key

		const cached = readCache(key)
		if (cached) {
			setName(cached.name)
			return
		}

		let cancelled = false
		api.activity
			.reverseGeocode(lat, lng)
			.then((res) => {
				if (cancelled) return
				writeCache(key, res.name)
				setName(res.name)
			})
			.catch(() => {})
		return () => {
			cancelled = true
		}
	}, [origin])

	return name
}
